<template>
    <div class="page">
        <div class="row">
            <div class="col-sm-12">
                <div class="page-title-box">
                    <h4
                        class="page-title"
                        id="page_title"
                        >
                        Address Map
                    </h4>
                </div>
            </div>
        </div>
        <div
            class="card card-body m-0"
            style="height: 600px;"
            >
            <div id="map" />
            <div>
                <div class="form-group row">
                    <div
                        class="truncate d-block d-sm-flex justify-content-between position-relative w-100 py-3 px-2 mx-2"
                        style="background-color: #e7e4e0;"
                        >
                        <div class="truncate">
                            <strong>Longitude:</strong> {{ form.address_longitude }}
                        </div>
                        <div class="truncate">
                            <strong>Latitude:</strong> {{ form.address_latitude }}
                        </div>
                    </div>
                </div>
                <div class="form-group row mb-3">
                    <label class="col-sm-5 col-lg-4 col-form-label">Area of Delivery:</label>
                    <div class="col-sm-7 col-lg-8">
                        <select
                            v-model="form.area_of_delivery"
                            class="mr-2 mt-1 mt-md-0 form-control"
                            title="filter"
                            @change="onChangeAreaOfDelivery"
                            >
                            <option :value="0">Kilometer(s)</option>
                            <option
                                v-for="area in area_options"
                                :value="area.value"
                                >
                                {{ area.name }}
                            </option>
                        </select>
                    </div>
                </div>
                <div class="text-center mt-3">
                    <button
                        @click="submit()"
                        class="btn btn-primary waves-effect waves-light"
                        type="button"
                        >
                        <i class="mdi mdi-content-save"/>
                        Save changes
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import MapboxGeocoder from "@mapbox/mapbox-gl-geocoder";
import 'mapbox-gl/dist/mapbox-gl.css';

export default {
    data() {
        return {
            map: {},
            mapStyle: "mapbox://styles/mapbox/streets-v11?optimize=true",
            center: [],
            accessToken: process.env.MIX_MAPBOX_ACCESS_TOKEN,
            geoJsonLayer: {
                id: 'circle-layer',
                type: 'circle',
                source: {
                    type: 'geojson',
                    data: {
                        type: 'FeatureCollection',
                        features: [
                            {
                                type: "Feature",
                                geometry: {
                                    type: "Point",
                                    coordinates: [],
                                },
                            }
                        ]
                    },
                },
                paint: {
                    'circle-color': '#007cbf',
                    'circle-opacity': 0.5,
                    'circle-radius': {
                        stops: [
                            [0, 0],
                            [20, 0],
                        ],
                        base: 2
                    },
                },
            },
            company: null,
            form: new Form({
                area_of_delivery: null,
                address_latitude: null,
                address_longitude: null,
            }),
            area_options: [
                {
                    name: '25 Kilometers',
                    value: 25,
                },
                {
                    name: '50 Kilometers',
                    value: 50,
                },
                {
                    name: '100 Kilometers',
                    value: 100,
                },
                {
                    name: '150 Kilometers',
                    value: 150,
                },
                {
                    name: '200 Kilometers',
                    value: 200,
                },
                {
                    name: '250 Kilometers',
                    value: 250,
                },
                {
                    name: '300 Kilometers',
                    value: 300,
                },
                {
                    name: '350 Kilometers',
                    value: 350,
                },
                {
                    name: '400 Kilometers',
                    value: 400,
                },
                {
                    name: '450 Kilometers',
                    value: 450,
                },
                {
                    name: '500 Kilometers',
                    value: 500,
                },
                {
                    name: '1000 Kilometers',
                    value: 1000,
                },
            ],
        };
    },
    mounted() {
        this.company = this.$store.state.user.companies[0];
        this.form = _.extend(this.form, _.pick(this.company, ['address_latitude', 'address_longitude', 'area_of_delivery']));
        this.center = [this.form.address_longitude, this.form.address_latitude];
        if (this.form.area_of_delivery && this.form.address_latitude) {
            this.geoJsonLayer.paint['circle-radius'] = {
                stops: [
                    [0, 0],
                    [20, this.metersToPixelsAtMaxZoom(parseFloat(this.form.area_of_delivery) * 1000,  this.form.address_latitude)],
                ],
                base: 2
            }
        }
        this.geoJsonLayer.source.data.features[0].geometry.coordinates = [this.form.address_longitude, this.form.address_latitude];
        this.createMap();
        this.mapMarker();
    },
    computed: {
        zoomLevel() {
            const area = parseFloat(this.form.area_of_delivery);
            let zoomLevelValue = 0;

            if (area >= 25 && area <= 50) zoomLevelValue = 7
            if (area > 50 && area <= 100) zoomLevelValue = 6
            if (area > 100 && area <= 200) zoomLevelValue = 5
            if (area > 200 && area <= 500) zoomLevelValue = 4
            if (area > 500) zoomLevelValue = 3

            return (!this.form.area_of_delivery || zoomLevelValue);
        },
    },
    methods: {
        async createMap() {
            try {
                mapboxgl.accessToken = this.accessToken;
                this.map = new mapboxgl.Map({
                    container: 'map',
                    style: this.mapStyle,
                    center: this.center.includes(null) ? [-105.750595856519, 55.5859012851966] : this.center,
                    zoom: this.center.includes(null) ? 2 : this.zoomLevel,
                });

                let geocoder = new MapboxGeocoder({
                    accessToken: this.accessToken,
                    mapboxgl: mapboxgl,
                    marker: false,
                    placeholder: "Search Locations",
                    zoom: this.zoomLevel,
                });

                this.map.addControl(geocoder);

                geocoder.on('result', (e) => {
                    this.center = e.result.center;
                    this.form.address_latitude = e.result.center[1];
                    this.form.address_longitude = e.result.center[0];

                    this.map.flyTo({
                        center: this.center,
                        zoom: this.zoomLevel,
                        essential: true
                    });

                    this.resetGeoJsonLayerCoordinates();
                });

                this.map.on('load', () => {
                    this.map.addLayer(this.geoJsonLayer);
                });
            } catch (error) {
                console.log("map error:", error);
            }
        },
        mapMarker() {
            const marker = new mapboxgl.Marker({
                draggable: true,
                color: "#D80739",
            })
                .setLngLat(this.center)
                .addTo(this.map);

            marker.on("dragend", (e) => {
                this.center = Object.values(e.target.getLngLat());
                this.form.address_latitude = this.center[1];
                this.form.address_longitude = this.center[0];

                this.resetGeoJsonLayerCoordinates();
            });

            this.$watch(
                () => [this.form.address_longitude, this.form.address_latitude],
                (newVal) => {
                    marker.setLngLat(newVal);
                    this.geoJsonLayer.source.data.features[0].geometry.coordinates = newVal;
                }
            );
        },
        submit() {
            this.form.validate()
                .then(() => {
                    this.$confirm('Are you sure to save changes?', 'Confirm', {
                        confirmButtonText: 'OK',
                        cancelButtonText: 'Cancel',
                        type: 'info'
                    }).then(() => {
                        App.putData('/web-api/company/' + this.company.id, this.form)
                            .then(() => {
                                this.$notify({
                                    title: 'Success',
                                    message: 'Successfully updated company address.',
                                    type: 'success'
                                });

                                this.resetGeoJsonLayerCoordinates();
                            })
                    });
                });
        },
        onChangeAreaOfDelivery() {
            this.map.flyTo({
                center: this.center,
                zoom: this.zoomLevel,
                essential: true
            });
            this.map.removeLayer('circle-layer');
            this.map.removeSource('circle-layer');
            this.geoJsonLayer.paint['circle-radius'] = {
                stops: [
                    [0, 0],
                    [20, this.metersToPixelsAtMaxZoom(parseFloat(this.form.area_of_delivery) * 1000,  this.form.address_latitude)],
                ],
                base: 2
            }
            this.map.addLayer(this.geoJsonLayer);
        },
        metersToPixelsAtMaxZoom(meters, latitude) {
            return meters / 0.075 / Math.cos(latitude * Math.PI / 180)
        },
        resetGeoJsonLayerCoordinates() {
            this.map.removeLayer('circle-layer');
            this.map.removeSource('circle-layer');
            this.geoJsonLayer.source.data.features[0].geometry.coordinates = [this.form.address_longitude, this.form.address_latitude];
            this.map.addLayer(this.geoJsonLayer);
        },
    },
};
</script>
<style>
@import "~@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css";
#map {
  height: 70vh;
}
.mapboxgl-ctrl-top-right{
    width: 96.5%;
}
.mapboxgl-ctrl-geocoder {
  max-width: 100%;
  width: 100%;
  box-shadow: none;
}
.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

@media (max-width: 425px) {
    .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
        margin-right: 6px;
    }
}
@media (max-width: 375px) {
    .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
        margin-right: 5px;
    }
}
@media (max-width: 320px) {
    .mapboxgl-ctrl-top-right .mapboxgl-ctrl {
        margin-right: 4px;
    }
}
</style>
